import {
  applicationFeesApiV2,
  chattelDetailsApi,
  documentApi,
  documentsApi,
  documentTypeCalculationFieldApi,
  documentWorksheetApi,
  investorApi,
  loanApplicationBorrowersApi,
  loanApplicationsApi,
  loanPropertyApi,
  postClosingApi,
} from 'app/services/api';
import _ from 'lodash';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';

export const saveDocumentWorksheetAction = catchAsyncActionError(params => async () => {
  const { data } = await documentWorksheetApi.saveWorksheet(params);

  return data;
});

/**
 * Resolves the application's existing worksheet document for a worksheet-enabled document type
 * (the Documents service keeps at most one per type + application), or null when none exists yet.
 */
export const findWorksheetDocumentAction = catchAsyncActionError(
  ({ documentTypeId, applicationId }) =>
    async () => {
      const { data } = await documentsApi.findDocuments({
        typeId: documentTypeId,
        metadata: `ApplicationId:${applicationId}`,
      });

      const worksheets = (_.isArray(data) ? data : []).filter(
        document => document?.typeId === documentTypeId && !document.isDeleted,
      );

      const newest = _.maxBy(
        worksheets,
        ({ lifeTimeInfo }) => lifeTimeInfo?.modifiedDate ?? lifeTimeInfo?.createdDate ?? '',
      );

      return newest ?? null;
    },
);

export const evaluateWorksheetCalculationFieldsAction = catchAsyncActionError(
  ({ documentTypeId, values }) =>
    async () => {
      const { data } = await documentTypeCalculationFieldApi.evaluateDocumentTypeCalculationFields({
        documentTypeId,
        values,
      });

      return data ?? [];
    },
);

export const recalculateDocumentCalculationFieldsAction = catchAsyncActionError(
  ({ documentId }) =>
    async () => {
      const { data } = await documentApi.recalculateDocumentCalculationFields({ documentId });

      return data ?? [];
    },
);

// A chattel loan has no ChattelDetails row until its first save: the GET answers with an empty body
// (204, or 404 on some paths) — both read as "no details yet".
const fetchWorksheetChattelDetails = async applicationId => {
  try {
    const { data } = await chattelDetailsApi.getChattelDetails({ applicationId });

    return _.isEmpty(data) ? null : data;
  } catch (error) {
    if (error?.response?.status !== HTTP_RESPONSE_STATUS_CODES.NOT_FOUND) {
      throw error;
    }

    return null;
  }
};

/**
 * Reads the loan data a worksheet drawer shows read-only beside the worksheet (LEN-132: the
 * Pre-Funding Phone Audit's borrowers, loan terms, home and location). Same endpoints the loan
 * editor reads, so the facts match what the loan shows. Raw responses; see buildWorksheetLoanFacts.
 */
export const getWorksheetLoanFactsAction = catchAsyncActionError(
  ({ applicationId }) =>
    async () => {
      const [borrowers, borrowersView, loanInfo, subjectProperty, chattelDetails] =
        await Promise.all([
          loanApplicationBorrowersApi.getBorrowersInfo({ applicationId }),
          loanApplicationBorrowersApi.getBorrowersTableData({ applicationId }),
          loanApplicationsApi.getLoanInfo({ applicationId }),
          loanPropertyApi.getSubjectPropertyEditData({ applicationId }),
          fetchWorksheetChattelDetails(applicationId),
        ]);

      return {
        borrowers: borrowers.data,
        borrowersView: borrowersView.data,
        loanInfo: loanInfo.data,
        subjectProperty: subjectProperty.data,
        chattelDetails,
      };
    },
);

// Post Closing answers with an empty GUID when no closing investor is set.
const NO_INVESTOR_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Resolves the loan's closing investor (Post Closing) for a new worksheet's defaults (LEN-201: its
 * Chattel max advance % prefills the Dealer Options Worksheet's Markup), or null when the loan has
 * no closing investor or it is no longer in the company's investor list.
 */
export const getWorksheetClosingInvestorAction = catchAsyncActionError(({ loanId }) => async () => {
  const { data: postClosing } = await postClosingApi.getPostClosing({ loanId });
  // The raw Post Closing response nests the investor (selectPostClosing flattens it for the tab).
  const investorId = postClosing?.closingInvestor?.investorId;

  if (!investorId || investorId === NO_INVESTOR_ID) {
    return null;
  }

  const { data: investors } = await investorApi.getInvestors();

  return (investors ?? []).find(investor => investor.investorId === investorId) ?? null;
});

/**
 * Reads the loan's fees as the Fees page shows them (sections A–H, prepaids, initial escrow) for a
 * new worksheet's fee defaults (LEN-194: the Chattel Funding Worksheet's wire calculation).
 */
export const getWorksheetLoanFeesViewAction = catchAsyncActionError(
  ({ applicationId }) =>
    async () => {
      const { data } = await applicationFeesApiV2.getFeesViewByApplicationId({ applicationId });

      return data ?? null;
    },
);
